import api from "./api";
import { listParams, ManagedUserFilters, ManagedUserRole } from "./users";
import { PaginatedResponse } from "@/types/borrower";
import { LoginResponse } from "@/types/auth";
import { publicApiFetch } from "@/utils/publicApi";

export type InvitationRole = ManagedUserRole;
export type InvitationStatus = "pending" | "expired" | "accepted" | "revoked";

export interface Invitation {
  id: number;
  email: string;
  role: InvitationRole;
  /** Broker invitations only; empty otherwise. */
  company_name: string;
  status: InvitationStatus;
  invited_by_email: string | null;
  expires_at: string;
  accepted_at: string | null;
  last_invitation_sent_at: string | null;
  created_at: string;
}

export interface InvitationPublicMeta {
  email: string;
  role: InvitationRole;
  /** Pre-fills the broker's company; theirs wins if they change it. */
  company_name: string;
  status: InvitationStatus;
  organization_name: string;
  /** Existing user is only joining an additional organization (no password setup). */
  adds_organization_only?: boolean;
}

export interface AcceptInvitationData {
  password?: string;
  confirm_password?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
}

export const invitationService = {
  // Admin (authenticated) endpoints, keyed by invitation id.
  /** Invites into the inviter's organization (their default one), chosen server side. */
  create: async (data: {
    email: string;
    role: InvitationRole;
    /** Brokers only. */
    company_name?: string;
  }): Promise<Invitation> => {
    const response = await api.post<Invitation>("/accounts/invitations/", data);
    return response.data;
  },

  listPending: async (filters: ManagedUserFilters): Promise<PaginatedResponse<Invitation>> => {
    const response = await api.get<PaginatedResponse<Invitation>>("/accounts/invitations/", {
      params: { ...listParams(filters), status: "pending" },
    });
    return response.data;
  },

  /** A new `email` gets a new link sent to it; `company_name` (brokers) just updates. */
  update: async ({
    id,
    data,
  }: {
    id: number;
    data: Partial<Pick<Invitation, "email" | "company_name">>;
  }): Promise<Invitation> => {
    const response = await api.patch<Invitation>(`/accounts/invitations/${id}/`, data);
    return response.data;
  },

  resend: async (id: number): Promise<Invitation> => {
    const response = await api.post<Invitation>(`/accounts/invitations/${id}/resend/`);
    return response.data;
  },

  revoke: async (id: number): Promise<Invitation> => {
    const response = await api.post<Invitation>(`/accounts/invitations/${id}/revoke/`);
    return response.data;
  },

  // Public (unauthenticated) endpoints, keyed by the raw token from the email link.
  getByToken: async (token: string): Promise<InvitationPublicMeta> => {
    return publicApiFetch<InvitationPublicMeta>(`/auth/invitations/${encodeURIComponent(token)}/`);
  },

  accept: async (token: string, body: AcceptInvitationData): Promise<LoginResponse> => {
    return publicApiFetch<LoginResponse>(`/auth/invitations/${encodeURIComponent(token)}/accept/`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};
